import { computed, ref } from 'vue'
import { ApiError, api } from '@/utils/apiClient'

// Reads the operator's onboarding step from the backend so the dashboard
// (or any other landing page) can offer "fortsett der du slapp" UX
// instead of dumping a half-onboarded operator on a dashboard that
// assumes setup is complete.
//
// The backend tracks state in `organizations.onboarding_step`. Frontend
// previously only WROTE this column on each setup step submit but never
// READ it back — so the data was there, the UX wasn't. This composable
// is the bridge.
//
// Why this isn't in `useOnboarding`: useOnboarding is the in-flow state
// (sessionStorage, transient). serverStep is the source of truth for
// "what's the operator's actual progress on the backend" and persists
// across browsers + devices. Two distinct concerns.

// Mirrors validOnboardingSteps in internal/admin/handler_onboarding.go.
// If you add a step here, add it there too — and update STEP_TO_ROUTE.
//
// 'subdomain' is the very first step (the slug claim that happens on
// the apex /get-started/subdomain page). By the time the operator
// reaches a tenant subdomain, the row has typically already advanced
// past it — but the value is reachable on the wire (the apex
// SubdomainView writes 'subdomain' as a no-op pre-state) and the
// dashboard guard must handle it the same as 'profile' (next
// incomplete step = profile).
//
// 'team' is a legacy value: it was a wizard step in the 6-step
// version, but the Brukere/Team consolidation retired it. Old DB
// rows still carry 'team' until the operator advances; we route
// them straight to availability so they don't hit a dead route.
export type ServerStep =
  | 'subdomain'
  | 'profile'
  | 'company'
  | 'services'
  | 'team'
  | 'availability'
  | 'calendar'
  | 'done'

// shouldBounceSetupRoute encodes the router-guard rule for #558: any
// /setup/* navigation while the org is already 'done' must bounce to
// /dashboard. The view that would otherwise render
// (SetupProfileView etc.) PUTs org-level brand data on submit, which
// is fine during fresh signup but corrupts the org when an invited
// admin lands there. The backend forward-only state machine on
// onboarding_step is the load-bearing defense; this is the UI half
// so the bad view never gets a chance to mount.
//
// '/setup/done' is excluded — it's the post-wizard confirmation page,
// intentionally reachable by a 'done' org on its way out.
//
// Falsy serverStep (null) means the API read failed or hasn't fired
// yet. Fall through so the signup flow (which legitimately needs the
// wizard before the first /api/admin/onboarding-step succeeds) isn't
// trapped behind the guard.
export function shouldBounceSetupRoute(path: string, serverStep: ServerStep | null): string | null {
  if (!path.startsWith('/setup/') || path === '/setup/done') return null
  if (serverStep === 'done') return '/dashboard'
  return null
}

// shouldBounceDashboardToSetup encodes the router-guard rule for #1232:
// a fresh operator whose onboarding_step is anything other than 'done'
// must not be allowed to reach /dashboard/* by typing the URL directly.
// The backend already enforces the matching gate on /api/admin/* via
// RequireOnboardingOrPasskey (internal/middleware/onboarding_factor.go);
// without this SPA half, the dashboard chrome paints and queues silent
// 403s from every per-component data call. Bouncing the navigation up
// front avoids that thrash entirely.
//
// Returns the path to bounce to, or null when the navigation should
// proceed unchanged. Fail-open on a null serverStep (probe failed or
// hasn't resolved yet): the backend gate is the load-bearing backstop
// and we'd rather paint a 403 toast than trap a legitimate operator
// behind a flaky probe.
//
// 'subdomain' routes to /setup/profile because the slug-claim step
// finishes on the apex (/get-started/subdomain) and the next thing
// the operator sees on the tenant subdomain is /setup/profile.
export function shouldBounceDashboardToSetup(
  path: string,
  serverStep: ServerStep | null,
): string | null {
  if (path !== '/dashboard' && !path.startsWith('/dashboard/')) return null
  if (!serverStep || serverStep === 'done') return null
  const route = STEP_TO_PATH[serverStep] ?? '/setup/profile'
  return route
}

const STEP_TO_ROUTE: Record<Exclude<ServerStep, 'done'>, string> = {
  subdomain: 'setup-profile',
  profile: 'setup-profile',
  company: 'setup-company',
  services: 'setup-services',
  team: 'setup-availability',
  availability: 'setup-availability',
  calendar: 'setup-calendar',
}

// STEP_TO_PATH mirrors STEP_TO_ROUTE but yields router paths instead of
// named routes. Router guards return path strings (the `to` argument is
// already a route; we want a redirect target the guard hands back to
// vue-router as a string). Keep the two in lockstep — if a step's
// route name changes, its path almost certainly changes too.
const STEP_TO_PATH: Record<Exclude<ServerStep, 'done'>, string> = {
  subdomain: '/setup/profile',
  profile: '/setup/profile',
  company: '/setup/company',
  services: '/setup/services',
  team: '/setup/availability',
  availability: '/setup/availability',
  calendar: '/setup/calendar',
}

const serverStep = ref<ServerStep | null>(null)
// org-level welcomed_at stamp (organizations.welcomed_at != NULL). Gates
// the post-setup welcome modal so it shows once across devices. Defaults
// true so a failed/blank read never pops the modal for an existing org.
const welcomed = ref(true)
const loaded = ref(false)
const loading = ref(false)

// invalidateServerStep drops the module-scoped cache so the next
// loadServerStep() call re-hits /api/admin/onboarding-step. Called from
// every setup view after a successful PUT to /api/admin/onboarding-step
// so that the moment the operator advances past 'calendar' (= done),
// the router's #1232 dashboard gate sees the fresh state on the next
// navigation instead of bouncing them back to /setup/calendar from a
// stale cache. Mirrors invalidateCurrentAdmin's pattern.
export function invalidateServerStep(): void {
  serverStep.value = null
  loaded.value = false
}

export function useOnboardingResume() {
  async function loadServerStep() {
    if (loaded.value || loading.value) return
    loading.value = true
    try {
      const res = await api.get<{ step: ServerStep; welcomed?: boolean }>(
        '/api/admin/onboarding-step',
      )
      serverStep.value = res.step
      welcomed.value = res.welcomed ?? true
      loaded.value = true
    } catch (e) {
      // Best-effort read. A failure here (network blip, server hiccup)
      // shouldn't block the dashboard — just leaves serverStep as null
      // and the resume banner stays hidden.
      serverStep.value = null
      // #2453 — an already-onboarded operator whose onboarding-step probe
      // is rejected with 401/403 (a session factor the endpoint won't
      // accept, or a session the client's refresh couldn't revive) must
      // not trigger a probe retry-storm: every dashboard-view mount and
      // route guard would re-fire loadServerStep and log another stray
      // 401. Treat an auth rejection as terminal for this tab so the probe
      // runs at most once. Transient failures (network / 5xx) stay
      // retryable so a mid-onboarding operator isn't stranded by one blip.
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        loaded.value = true
      }
    } finally {
      loading.value = false
    }
  }

  // Null when:
  //   - not yet loaded
  //   - load failed
  //   - operator has finished onboarding (step === 'done')
  // Otherwise: the step name to resume from.
  const pendingStep = computed<Exclude<ServerStep, 'done'> | null>(() => {
    if (!serverStep.value || serverStep.value === 'done') return null
    return serverStep.value
  })

  const resumeRouteName = computed(() =>
    pendingStep.value ? STEP_TO_ROUTE[pendingStep.value] : null,
  )

  // Hybrid strategy: redirect to the resume route on the *first* dashboard
  // land per tab session, then fall back to the banner for subsequent
  // visits in the same tab.
  //
  // Why hybrid:
  //   - The dominant entry to /dashboard with pendingStep !== null is a
  //     magic-login click ("continue setup"). Those operators want to be
  //     bounced straight back to the step they left at — banner-then-click
  //     is friction.
  //   - But after that first redirect, an operator might intentionally
  //     navigate to /dashboard mid-setup to glance at the empty state
  //     (preview their address, copy the share link, sanity-check). A
  //     hard redirect on every visit blocks that.
  //
  // Sentinel lives in sessionStorage so it's per-tab — open dashboard in
  // a new tab and you get the redirect again, which matches mental model
  // (each tab is its own session). It does NOT need invalidation when the
  // step advances: redirected ⇒ on a setup view ⇒ this code doesn't run
  // there.
  const REDIRECTED_KEY = 'stemne_resume_redirected'

  function maybeRedirect(routerReplace: (n: { name: string }) => void) {
    if (!pendingStep.value || !resumeRouteName.value) return
    try {
      if (sessionStorage.getItem(REDIRECTED_KEY) === '1') return
      sessionStorage.setItem(REDIRECTED_KEY, '1')
    } catch {
      // sessionStorage unavailable (private mode quirks, very old browsers).
      // Fall through to redirect — the banner is also still rendered, so
      // worst case the operator gets a one-time redirect every visit. Not
      // great but no worse than the safe default.
    }
    routerReplace({ name: resumeRouteName.value })
  }

  return {
    serverStep,
    welcomed,
    pendingStep,
    resumeRouteName,
    loaded,
    loading,
    loadServerStep,
    maybeRedirect,
  }
}
